import Image from "next/image";

import BlogDetailController from "../controller/blogsDetail.controller";
import { Category } from "../types/category.interface";
import { GetServerSideProps, NextPage } from "next";
import { Media } from "../types/media.interface";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { Suspense } from "react";
import Head from "next/head";

import Link from "next/link";

const RecentBlogCard = dynamic(() => import("../components/Blog/RecentCard"), {
  suspense: true,
});
const ShareButton = dynamic(() => import("../components/ShareButton"), {
  suspense: true,
});

interface BlogDetailProps {
  blog: {
    id: number;
    slug: string;
    title: string;
    content: string;
    imageAlt: string;
    metaTitle: string;
    metaDesc: string;
    createdAt: Date;
    categories: Category[];
    image: Media;
    author: any;
    jsonLD: string;
  };
  recentBlog: any;
}

const BlogDetail: NextPage<BlogDetailProps> = ({ blog, recentBlog }) => {
  const createDate = new Date(blog.createdAt);
  const router = useRouter();
  return (
    <>
      <Head>
        <title>{`${blog.title} - BungalowFinder`}</title>
        <meta name="title" property="og:title" content={blog.metaTitle} />
        <meta
          name="description"
          property="og:description"
          content={blog.metaDesc}
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content={`https://api.bungalowfinder.ca/` + blog.image.images.og.url}
        />
        <meta
          property="og:image:secure_url"
          content={`https://api.bungalowfinder.ca/` + blog.image.images.og.url}
        />

        <meta
          property="og:url"
          content={`https://bungalowfinder.ca` + router.asPath}
        />

        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={blog.metaTitle} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={blog.metaTitle} />
        <meta name="twitter:description" content={blog.metaDesc} />
        <meta
          name="twitter:image"
          content={`https://api.bungalowfinder.ca/` + blog.image.images.og.url}
        />
        {blog.jsonLD && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: blog.jsonLD,
            }}
          ></script>
        )}
      </Head>
      <div className="mx-auto container flex flex-col flex-wrap py-2">
        {/* Category */}
        <div className="flex flex-col items-baseline gap-2 my-2">
          <div className="flex gap-1 flex-wrap px-2">
            {blog.categories &&
              blog.categories.map((cat) => (
                <Link
                  key={cat.id}
                  href={`/category/${cat.category.split(" ").join("-")}`}
                >
                  <span className="capitalize bg-primary rounded-md text-white px-3 text-xs md:text-sm py-1 shadow-lg hover:underline cursor-pointer">
                    {cat.category}
                  </span>
                </Link>
              ))}
          </div>
          <hr className="w-full border-black py-1" />
        </div>
        <div className="flex flex-wrap">
          <div className="w-full lg:w-2/3 px-2 lg:pr-8">
            <div className="flex flex-col gap-2">
              <h1 className="md:text-2xl text-xl font-semibold capitalize">
                {blog.title}
              </h1>
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-400">
                  {Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(createDate)}
                </p>
                <Suspense fallback={<>Loading....</>}>
                  <ShareButton />
                </Suspense>
              </div>
            </div>
            <div className="mt-2 relative w-full aspect-video">
              <Image
                src={
                  "https://api.bungalowfinder.ca" + blog.image.images.original.url
                }
                layout="fill"
                sizes="(max-width: 768px) 100vw, 500px"
                priority
                className="lg:min-h-[420px] h-auto rounded-md"
                alt={blog.imageAlt}
              />
            </div>

            {/* BLOG */}

            <div
              className="text-justify pt-4 element"
              dangerouslySetInnerHTML={{
                __html: blog.content,
              }}
            />
          </div>
          <div className="w-full sticky top-0 lg:w-1/3 flex flex-col px-2 gap-2">
            <h2 className="text-xl md:text-lg font-medium">Recent Blogs</h2>
            <div className="flex flex-wrap gap-6 w-full py-2">
              <Suspense
                fallback={
                  <>
                    <p>Loading...</p>
                  </>
                }
              >
                {recentBlog.data.map((item: any) => (
                  <RecentBlogCard key={item.id} item={item} />
                ))}
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export const getServerSideProps: GetServerSideProps = async (context) => {
  return await BlogDetailController(context);
};

export default BlogDetail;
